import { Link } from 'react-router-dom'
import { ChevronDown, Users, HelpCircle, Mail, User as UserIcon, LogIn } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useUser } from '@/hooks/use-user'
import { usePendingMessageReports } from '@/features/admin/_shared/usePendingMessageReports'
import { useGetConfigAll } from '@/api/generated/config/config'
import { getMessagesUnreadCount } from '@/api/generated/messages-messages/messages-messages'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { parseDashboardConfig } from '@/features/user/dashboard/config'
import { deleteCookie } from '@/lib/cookies'
import type { User } from '@/api/types'

// Shared button styling for the top-nav triggers + plain links.
// `text-xs sm:text-sm` and `px-2 sm:px-3` keep things compact on phones
// so the four top items (+ logo + user) can fit a 360px viewport without
// triggering a visible scroll affordance on the nav rail.
const triggerCls =
  'inline-flex items-center gap-0.5 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white/80 hover:text-white transition-colors whitespace-nowrap cursor-pointer outline-none'

// `href` = external, opens in a new tab. `to` = internal, router navigation
// (no full page reload). Exactly one of the two is set per entry.
interface CommunityItem {
  label: string
  href?: string
  to?: string
}

const communityItems: CommunityItem[] = [
  { label: 'Blog', to: '/blog' },
  { label: 'Forums', href: 'https://forums.basehit.com/' },
  { label: 'Discord', href: 'https://discord.gg/RwjTgJ93eU' },
  { label: 'Facebook', href: 'https://www.facebook.com/basehitgame' },
  { label: 'YouTube', href: 'https://www.youtube.com/@basehitgame' },
]

function LeaguesDropdown({ user }: { user: User }) {
  // Mirror the dashboard's viewing order. The user reorders their leagues on the
  // dashboard Organize dialog (persisted as `leagueOrder` in their user config),
  // and this dropdown honors that exact order so the two surfaces agree. Leagues
  // the user hid on the dashboard (`hiddenLeagues`) sink to the BOTTOM here —
  // never filtered out — because the dropdown is the primary way to reach every
  // team, so hiding a league must de-prioritize it without orphaning its team
  // from the top nav. Within each group (visible, then hidden) the leagueOrder
  // still applies. With no saved order (never organized), both surfaces fall back
  // to alphabetical, so they still match.
  const { data: configsData } = useGetConfigAll()
  const dashConfig = parseDashboardConfig(configsData, user.id)
  const order = dashConfig.leagueOrder ?? []
  const hidden = new Set(dashConfig.hiddenLeagues ?? [])
  const orderedLeagues = [...(user.leagues ?? [])].sort((a, b) => {
    const aHidden = hidden.has(a.id)
    const bHidden = hidden.has(b.id)
    if (aHidden !== bHidden) return aHidden ? 1 : -1
    const aIdx = order.indexOf(a.id)
    const bIdx = order.indexOf(b.id)
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx
    if (aIdx !== -1) return -1
    if (bIdx !== -1) return 1
    return a.name.localeCompare(b.name)
  })

  // Build league+team pairs from user data so each team gets its own row
  // under the league name (matches the dashboard mental model: a team
  // belongs to a league, you click whichever you want to drill into).
  const allLeagueTeams = orderedLeagues.flatMap((league) =>
    league.teams.map((team) => ({ league, team })),
  )
  // Split the sandbox out so it sits below a divider in its own amber-tinted
  // section — it's a practice/learning space, not a real franchise, and
  // shouldn't read as just another team in the list.
  const leagueTeams = allLeagueTeams.filter(({ league }) => league.leagueType !== 'sandbox')
  // Honor the "hide my sandbox" setting here too — same gate as the "My Sandbox"
  // account-menu link below. When hidden, the sandbox drops out of the Leagues
  // dropdown entirely (no amber section).
  const sandboxTeams = user.settings?.hideSandbox
    ? []
    : allLeagueTeams.filter(({ league }) => league.leagueType === 'sandbox')

  return (
    <DropdownMenu>
      {/* Leagues is the primary nav item (always text, never icon-only)
       *  since it's the deepest entry point into the app. Bolder weight
       *  + full-opacity white separates it from the secondary items
       *  which use `text-white/80` and `font-medium`. */}
      <DropdownMenuTrigger className={`${triggerCls} !text-gold !font-semibold hover:!text-gold/80`}>
        Leagues
        <ChevronDown size={12} className="hidden sm:block" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[220px]">
        <DropdownMenuItem asChild>
          <Link to={`/user/${user.id}`}>Dashboard</Link>
        </DropdownMenuItem>
        {leagueTeams.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {leagueTeams.map(({ league, team }) => (
              <DropdownMenuItem key={team.id} asChild className="py-1.5">
                <div>
                  <a
                    href={`/league/${league.id}/view`}
                    className="block text-[11px] text-gray-400 hover:text-gray-600 no-underline leading-tight"
                  >
                    {league.name}
                  </a>
                  <a
                    href={`/team/${team.id}/view`}
                    className="block text-sm text-gray-700 hover:text-gray-900 no-underline leading-tight"
                  >
                    {team.fullName}
                  </a>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
        {sandboxTeams.length > 0 && (
          <>
            <DropdownMenuSeparator className="bg-amber-300" />
            <div className="px-2 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
              Sandbox
            </div>
            {sandboxTeams.map(({ league, team }) => (
              <DropdownMenuItem key={team.id} asChild className="py-1.5">
                <div>
                  <a
                    href={`/league/${league.id}/view`}
                    className="block text-[11px] text-amber-500/80 hover:text-amber-700 no-underline leading-tight"
                  >
                    {league.name}
                  </a>
                  <a
                    href={`/team/${team.id}/view`}
                    className="block text-sm text-amber-700 hover:text-amber-800 no-underline leading-tight font-medium"
                  >
                    {team.fullName}
                  </a>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
        <DropdownMenuSeparator className="bg-slate-300" />
        <DropdownMenuItem asChild>
          <a href="/leagues">Find a League or Team</a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href="/teams/create">Create a New Team</a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function CommunityDropdown() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={triggerCls}>
        <Users size={22} className="sm:hidden" />
        <span className="hidden sm:inline">Community</span>
        <ChevronDown size={12} className="hidden sm:block" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]">
        {/* Ours first, then a separator, then the wider community list --
            most of those leave the site (new tab); Blog is in-app content
            too, so it renders as a router Link like this one does. */}
        <DropdownMenuItem asChild>
          <Link to="/users">Find a Manager</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {communityItems.map((item) =>
          item.to ? (
            <DropdownMenuItem key={item.label} asChild>
              <Link to={item.to}>{item.label}</Link>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem key={item.label} asChild>
              <a href={item.href} target="_blank" rel="noopener noreferrer">
                {item.label}
              </a>
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function UserDropdown({ user, onLogout }: { user: User; onLogout: () => void }) {
  // No-ops (and never fires) for non-admins — see usePendingMessageReports.
  const pendingReports = usePendingMessageReports()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={`${triggerCls} relative`}>
        {!user.hasVerifiedEmail && (
          <span
            className="absolute right-0.5 top-1 h-2 w-2 rounded-full bg-yellow-400 ring-2 ring-navy"
            aria-label="Email verification required"
          />
        )}
        <UserIcon size={22} className="sm:hidden" />
        <span className="hidden sm:inline">Profile</span>
        <ChevronDown size={12} className="hidden sm:block" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        {/* Username shown as a non-clickable header so the user can
         *  confirm whose menu they're in. The trigger button itself
         *  uses a fixed "Profile" label — this avoids the nav rail
         *  shifting widths based on how long the username is. */}
        <div className="px-2 py-1.5 text-base font-semibold text-slate-900 truncate">
          {user.username}
        </div>
        <DropdownMenuSeparator />
        {/* Dashboard is the entry to your user area; Records & Timeline are reached
         *  from the on-page nav rail there (the rail also browses other users'),
         *  so they're intentionally NOT duplicated in this menu. */}
        <DropdownMenuItem asChild>
          <Link to={`/user/${user.id}`}>Dashboard</Link>
        </DropdownMenuItem>
        {/* Team Requests = your own outgoing requests to join teams. Rarely used,
         *  so it lives here in the Profile menu rather than the user nav rail.
         *  (Commish-side incoming requests live on League → Team Ownership + the
         *  dashboard commissioner-icon badge — a different surface.) */}
        <DropdownMenuItem asChild>
          <Link to={`/user/${user.id}/teamrequests`}>Team Requests</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to={user.hasVerifiedEmail ? `/user/${user.id}/settings` : `/user/${user.id}/settings#change-email`}
            className="flex items-center justify-between gap-2"
          >
            Settings
            {!user.hasVerifiedEmail && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700">
                <span className="h-2 w-2 rounded-full bg-yellow-400" />
                Verify email
              </span>
            )}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href="/user/referral">Refer a Friend</a>
        </DropdownMenuItem>
        {user.hasSandbox && !user.settings?.hideSandbox && user.sandboxLeagueId != null && (
          <DropdownMenuItem asChild>
            <a href={`/league/${user.sandboxLeagueId}/view`}>My Sandbox</a>
          </DropdownMenuItem>
        )}
        {(user.isAdmin || user.isSuperUser) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/admin" className="flex items-center gap-2">
                Admin Tools
                {/* Message reports are a silent queue — nothing emails or
                    notifies on a new one — so the count rides the only
                    always-present route into the admin area. */}
                {pendingReports > 0 && (
                  <span
                    className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white"
                    aria-label={`${pendingReports} pending message report${pendingReports === 1 ? '' : 's'}`}
                  >
                    {pendingReports}
                  </span>
                )}
              </Link>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout}>Log Out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function Header() {
  const { user, isAuthenticated } = useUser()

  // Unread-messages badge poll. `enabled` keeps this off entirely for
  // logged-out visitors; the 60s interval + refetchOnWindowFocus keep the
  // count fresh without the user having to open /messages. Task 9 (the
  // /messages page) invalidates this exact queryKey after send/read so the
  // badge updates immediately on top of the poll.
  const unreadQuery = useQuery({
    queryKey: ['messages', 'unreadCount'],
    queryFn: ({ signal }) => getMessagesUnreadCount(signal),
    enabled: isAuthenticated && !!user,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
  const unreadCount = unreadQuery.data?.unread ?? 0

  function handleLogout() {
    // Consolidation retired the Classic ASP layer, so there's no server-side
    // session left to clear — the only auth is the JWT in the AccessToken
    // cookie. Drop it, then full-page nav to '/': now cookie-less, the API's
    // root branch serves the marketing landing.
    deleteCookie('AccessToken')
    window.location.href = '/'
  }

  function handleLoginClick() {
    document.dispatchEvent(new CustomEvent('open-login-modal'))
  }

  // Single always-visible row: logo (pinned left) + nav items (middle,
  // overflow-x-auto as a safety net for the smallest viewports) + user
  // menu (pinned right). The hamburger / MobileNav drawer pattern was
  // dropped — discoverability is poor and a stat-heavy app benefits from
  // always-visible top-level nav. `[scrollbar-width:none]` +
  // `[&::-webkit-scrollbar]:hidden` hides the scroll UI so even when the
  // nav has to scroll on a 320px device it doesn't read as a second
  // scroll bar competing with the page-level secondary nav below.
  return (
    <header className="sticky top-0 z-50 bg-navy landscape-short:static">
      <div className="mx-auto max-w-7xl px-3 sm:px-4">
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Logo — smaller on mobile (h-8) to claw back ~8px for the
           *  nav items. Tap goes to dashboard when authenticated, home
           *  otherwise. */}
          {isAuthenticated && user ? (
            <Link to={`/user/${user.id}`} className="shrink-0 py-2">
              <img
                src={`${import.meta.env.BASE_URL}images/logo-white.webp`}
                alt="BaseHit"
                className="h-8 sm:h-10 w-auto"
              />
            </Link>
          ) : (
            <a href="/" className="shrink-0 py-2">
              <img
                src={`${import.meta.env.BASE_URL}images/logo-white.webp`}
                alt="BaseHit"
                className="h-8 sm:h-10 w-auto"
              />
            </a>
          )}

          {/* Single nav rail with all the top-level items. `flex-1
           *  justify-around` spreads them evenly across the space the
           *  logo doesn't claim, so spacing stays consistent regardless
           *  of which items are visible (3 items when logged out, 4 when
           *  logged in). On mobile each item shows an icon; on `sm+` it
           *  swaps to a text label. The icons are recognizable enough
           *  that we don't need labels at phone widths (Trophy/Users/
           *  HelpCircle/User are universal app conventions). */}
          <nav className="flex flex-1 items-center justify-around sm:justify-end sm:gap-1">
            {isAuthenticated && user && <LeaguesDropdown user={user} />}
            <CommunityDropdown />
            <Link to="/help" className={triggerCls}>
              <HelpCircle size={22} className="sm:hidden" />
              <span className="hidden sm:inline">How to Play</span>
            </Link>
            {isAuthenticated && user && (
              <Link to="/messages" className={`${triggerCls} relative`} aria-label="Messages">
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 right-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-navy">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
                <Mail size={22} className="sm:hidden" />
                <span className="hidden sm:inline">Messages</span>
              </Link>
            )}
            {isAuthenticated && user && <NotificationBell />}
            {isAuthenticated && user ? (
              <UserDropdown user={user} onLogout={handleLogout} />
            ) : (
              // Logged-out: a single Log In trigger sits in the same
              // slot as Profile so the rail's rightmost item is stable.
              // Sign Up lives inside the login modal.
              <button onClick={handleLoginClick} className={triggerCls}>
                <LogIn size={22} className="sm:hidden" />
                <span className="hidden sm:inline">Log In</span>
              </button>
            )}
          </nav>
        </div>
      </div>
    </header>
  )
}
